import currency from "currency.js";
import { floorToDecimalsString } from "../../../utils/decimalString";
import { SPACING } from "../../../constants/design/spacing";
import { apiNum } from "../../../utils/math";
import {
  formatKnownValue,
  UNKNOWN_DISPLAY,
} from "../../../utils/unknownNumbers";
import { Divider } from "../../shared/Divider";
import {
  AccountEquitySectionWrapper,
  SectionInfoLabelText,
  SectionInfoRowItem,
  SectionInfoValueText,
  SectionTitleText,
} from "./style";
import { PerpsTradeFormCtx } from "./PerpsTradeForm.ctx";

export default function PerpsTradeFormAccountEquity({
  ctx,
}: {
  ctx: PerpsTradeFormCtx;
}) {
  const { isPerp, accountData } = ctx;
  // Unknown (null) ratio / leverage stay unknown: `?? 0` printed "0.00%" and
  // "0.00x", an account that looks unlevered and safe.
  /**
   * A balance, floored rather than rounded (46e57db: never show more than the
   * account has), and "-" when it is unknown.
   *
   * `formatKnownValue` is right about the unknown but rounds, and
   * `floorToDecimalsString` is right about the floor but turns a null into
   * "0.00" -- an absent balance indistinguishable from an empty one. Neither is
   * correct alone for money.
   */
  const balanceText = (value?: string | null) =>
    apiNum(value) === undefined
      ? UNKNOWN_DISPLAY
      : currency(floorToDecimalsString(value, 2), { precision: 2 }).format();

  const crossMarginRatio = apiNum(accountData?.cross_margin_ratio);
  const crossAccountLeverage = apiNum(accountData?.cross_account_leverage);

  return (
    <>
      {/* ── Account Equity + Perps Overview ── */}
      {isPerp && (
        <>
          <Divider direction="horizontal" size="100%" />
          <AccountEquitySectionWrapper>
            <SectionTitleText>Account Equity</SectionTitleText>
            <SectionInfoRowItem>
              <SectionInfoLabelText $underline>Perps</SectionInfoLabelText>
              <SectionInfoValueText>
                {/* Equity, not available balance. Both rows read
                    `available_balance`, so two differently-labelled figures
                    were identical and neither was what its label claimed. */}
                {balanceText(accountData?.equity)}
              </SectionInfoValueText>
            </SectionInfoRowItem>

            <SectionTitleText style={{ marginTop: SPACING.four }}>
              Perps Overview
            </SectionTitleText>
            <SectionInfoRowItem>
              <SectionInfoLabelText $underline>Balance</SectionInfoLabelText>
              <SectionInfoValueText>
                {balanceText(accountData?.balance)}
              </SectionInfoValueText>
            </SectionInfoRowItem>
            <SectionInfoRowItem>
              <SectionInfoLabelText>Unrealized PNL</SectionInfoLabelText>
              <SectionInfoValueText>
                {/* null while a position is unpriced: "-", never "$0.00". */}
                {formatKnownValue(accountData?.unrealized_pnl, 2)}
              </SectionInfoValueText>
            </SectionInfoRowItem>
            <SectionInfoRowItem>
              <SectionInfoLabelText $underline $teal>
                Cross Margin Ratio
              </SectionInfoLabelText>
              <SectionInfoValueText $teal>
                {crossMarginRatio === undefined
                  ? UNKNOWN_DISPLAY
                  : `${(crossMarginRatio * 100).toFixed(2)}%`}
              </SectionInfoValueText>
            </SectionInfoRowItem>
            <SectionInfoRowItem>
              <SectionInfoLabelText $underline>
                Maintenance Margin
              </SectionInfoLabelText>
              <SectionInfoValueText>
                {formatKnownValue(
                  accountData?.maintenance_margin_used ??
                    accountData?.maintenance_margin,
                  2
                )}
              </SectionInfoValueText>
            </SectionInfoRowItem>
            <SectionInfoRowItem>
              <SectionInfoLabelText $underline>
                Cross Account Leverage
              </SectionInfoLabelText>
              <SectionInfoValueText>
                {crossAccountLeverage === undefined
                  ? UNKNOWN_DISPLAY
                  : `${crossAccountLeverage.toFixed(2)}x`}
              </SectionInfoValueText>
            </SectionInfoRowItem>
          </AccountEquitySectionWrapper>
        </>
      )}
    </>
  );
}
